const guide = (setup, app, prompt, result, options = {}) => ({ setup, app, prompt, result, ...options });

export const featureGuides = {
  'the-strategy': guide(
    'Create a plan from your opportunity, then activate the generated strategy.',
    'Open Strategy to inspect the live product intent, assumptions, positioning, and constraints. Use Chat to challenge or refine it.',
    'Read the current Pabs strategy. Summarise the product intent, strongest assumptions, constraints, and open decisions relevant to [work].',
    'The answer should cite current strategy context and distinguish approved direction from assumptions or gaps.'
  ),
  brief: guide(
    'Create or open a plan and complete the Brief with the audience, problem, evidence, goals, and constraints.',
    'Edit the Brief directly or ask Pabs Chat to sharpen a weak section before regenerating affected outputs.',
    'Use Pabs to review the Brief for [initiative]. Identify missing evidence, vague assumptions, conflicting constraints, and the next questions to resolve.',
    'Check that the Brief reflects your input and that proposed changes have not silently changed approved intent.'
  ),
  'product-strategy': guide(
    'Generate Product Strategy from an active Brief and review the first version before sharing it.',
    'Open Product Strategy to refine the narrative, positioning, strategic choices, and supporting evidence.',
    'Use the current Pabs Product Strategy to brief [team or agent] on [initiative]. Include the intended outcome, strategic choices, boundaries, and unresolved decisions.',
    'The response should be usable as direction and remain traceable to the current strategy version.'
  ),
  'competitor-analysis': guide(
    'Add known competitors and useful market sources, then generate the analysis from the plan.',
    'Review the comparison, challenge unsupported claims, and promote useful findings into strategy decisions.',
    'Use Pabs to compare [competitors] for [audience and problem]. Focus on meaningful alternatives, evidence, differentiation, and implications for our strategy.',
    'Check sources, dates, confidence, and whether the findings actually change a decision.'
  ),
  'design-vision': guide(
    'Activate strategy, then define the experience principles and qualities the product should preserve.',
    'Use Design Vision to set the experience direction that briefs and Design Authority reviews should apply.',
    'Read the Pabs Design Vision for this plan. Translate it into concrete experience principles and constraints for [feature or journey].',
    'The result should connect abstract direction to observable behavior without inventing new brand rules.'
  ),
  decisions: guide(
    'Activate a strategy so Pabs can surface blockers, open questions, recommendations, and choices requiring an owner.',
    'Open Decisions, inspect the evidence and options, then approve or record the decision in Pabs.',
    'Use Pabs to help decide [decision]. Compare the options with our strategy, evidence, constraints, and affected users. Recommend a direction and state what could change it.',
    'Confirm that the owner, rationale, consequences, and reconsideration signal are captured.'
  ),
  'design-authority': guide(
    'In the Pabs plan, define what Design Authority covers, who is accountable, and which sources govern the work.',
    'The accountable lead uses Pabs to configure the authority, follow the work performed by teams and agents, and make decisions that require human judgment.',
    'In this agent tool, use Pabs Design Authority to review [artifact] before [next stage]. Apply the approved product intent, design rules, accessibility expectations, and evidence requirements.',
    'The lead can trace the job, evidence, findings, and decisions in Pabs. Consequential approval remains with the authorised person.',
    {
      steps: [
        {
          number: '1',
          title: 'Set it up in Pabs',
          body: 'Define the scope, lifecycle stages, accountable lead, and governing sources for this plan.',
          href: '#set-it-up-for-a-plan',
          cta: 'See setup steps',
        },
        {
          number: '2',
          title: 'Connect agent tools',
          body: 'Add the plan-specific Pabs connection to Claude, Cursor, or another AI client where the work happens.',
          href: '#connect-your-agent-tools',
          cta: 'Connect a tool',
        },
        {
          number: '3',
          title: 'Stay in control',
          body: 'Use Pabs to follow jobs, evidence, and decisions while the team and its agents do the delivery work.',
          href: '#follow-the-work-in-pabs',
          cta: 'See the lead workflow',
        },
      ],
      appLabel: 'For the accountable lead',
      clientLabel: 'For an agent tool',
      appEyebrow: 'In the Pabs interface',
    }
  ),
  'action-plan': guide(
    'Generate an Action Plan from the current Brief and Product Strategy.',
    'Review priorities, owners, dependencies, and timing. Edit or regenerate only the parts that changed.',
    'Use Pabs to prepare an action plan for [outcome and period]. Respect [constraints]. Include sequence, owner, dependency, evidence, and completion criteria.',
    'Check that every action supports an approved outcome and that dependencies and ownership are explicit.'
  ),
  'calendar-view': guide(
    'Create an Action Plan with owners and timing so work can appear in Calendar.',
    'Use Calendar to inspect load, sequence, milestones, and conflicts. Move work only when the underlying plan still holds.',
    'Read the Pabs Action Plan and Calendar. Identify timing conflicts, missing dependencies, overloaded owners, and milestones at risk for [period].',
    'Confirm that schedule changes also update the durable plan rather than becoming a separate timeline.'
  ),
  exports: guide(
    'Activate the plan and choose the document needed for the audience or delivery stage.',
    'Generate the document in Pabs, review its scope and version, then export or share it.',
    'Use Pabs to prepare a [document type] for [audience and purpose]. Use only approved strategy and decisions, and flag missing information.',
    'Check the document version, included sources, unresolved gaps, and whether it matches the current plan.'
  ),
  sharing: guide(
    'Choose which plan views can be shared and create a controlled link.',
    'Preview the shared view before sending it, then revoke or update access when the audience changes.',
    'Use Pabs to summarise the current plan for [audience]. Include [allowed areas] and exclude [protected areas].',
    'Verify what recipients can see and that protected strategy, evidence, or personal data is not exposed.'
  ),
  'design-authority-start': guide(
    'Complete Design Authority setup and connect the sources required for the selected lifecycle stage.',
    'Ask Pabs to prepare a design brief before concepts or implementation begin.',
    'Prepare a Design Authority brief for [initiative]. Include journeys, states, design-system constraints, accessibility expectations, protected decisions, and evidence needed for review.',
    'Check that the brief names unknowns and review criteria instead of pretending the design is already resolved.'
  ),
  'design-authority-knowledge': guide(
    'Connect existing product, research, design-system, repository, and policy sources to the plan.',
    'Review source coverage in Design Authority and add durable documents for material knowledge gaps.',
    'Use Pabs to identify missing product and design knowledge for [scope]. Propose the durable files or sources we should create, including a design.md where useful.',
    'Confirm that new documentation records approved decisions, source dates, and unresolved conflicts.'
  ),
  'design-authority-improve': guide(
    'Select an existing journey or experience and provide the current artifact, evidence, and protected scope.',
    'Run an audit, inspect findings, then create the smallest correction brief for approved issues.',
    'Audit [journey] from [start] to [end]. Focus on [areas], use [evidence], and preserve [protected scope]. Prepare correction work only for verified findings.',
    'Check that findings show evidence and severity, and that correction work does not expand the scope.'
  ),
  'design-authority-check': guide(
    'Provide the artifact or build, approved reference, intended next stage, and required evidence.',
    'Run the relevant review job and make the promotion decision in Pabs.',
    'Review [artifact or build] before [next stage]. Recommend promote, promote with constraints, revise, or reject. Separate verified findings from anything you could not inspect.',
    'Confirm the decision, evidence, exceptions, and follow-up work are attached to the review.'
  ),
  pulse: guide(
    'Choose the plan signals, cadence, recipients, and tone for a product pulse.',
    'Preview a pulse in Pabs, adjust its focus, then enable delivery when the signal is useful.',
    'Use Pabs to produce a pulse for [audience and period]. Cover changed evidence, decisions, risks, drift, and the next intervention that needs attention.',
    'Check that the pulse highlights change and consequence rather than repeating a static status summary.'
  ),
  'jobs-and-routines': guide(
    'Choose an existing Pabs job, define its scope and evidence, then run it now or give it a schedule.',
    'Use Jobs for durable outcomes and routines for repeated execution. Review every consequential result before it changes direction.',
    'Schedule [job] every [cadence] for [scope]. Use [sources], report [signals], and require approval before [consequential action].',
    'Verify the next run, scope, owner, notification, and approval boundary.'
  ),
  'learnings-engine-concept': guide(
    'Activate a plan and connect the places where research, delivery evidence, and market signals appear.',
    'Capture evidence in Pabs, review its proposed impact, and approve only the strategy changes you accept.',
    'Use Pabs to evaluate this learning: [evidence]. Show which assumptions, decisions, and strategy sections it strengthens, weakens, or contradicts.',
    'Check evidence quality and approve, edit, or reject each proposed change separately.',
    { presentation: 'reference' }
  ),
  'learnings-capturing': guide(
    'Connect the plan to the client where evidence appears, or open Add Learning in Pabs.',
    'Add the evidence, source, date, and context. Pabs will propose what it may affect.',
    'Report this learning to Pabs: [evidence]. Source: [source]. Context: [context]. Do not change strategy until the impact is reviewed.',
    'Confirm the learning is stored once, attributed correctly, and queued for review.'
  ),
  'learnings-reviewing': guide(
    'Capture at least one learning so Pabs can generate an impact proposal.',
    'Open the learning review, inspect evidence and affected fields, then approve, edit, or reject each change.',
    'Use Pabs to review unprocessed learnings for [plan or period]. Group duplicates and show the proposed strategy changes with evidence and confidence.',
    'Confirm that only approved changes reach the strategy and rejected evidence remains traceable.'
  ),
  'design-authority-maintain': guide(
    'Choose an enabled Design Authority job and define the area, cadence, sources, and owner.',
    'Create a routine in Pabs, review each run, and adjust the scope when the product or risk changes.',
    'Schedule a weekly [Design Authority job] for [scope]. Check design drift, accessibility regression, unresolved exceptions, and missing evidence.',
    'Verify that each run produces a reviewable result and never promotes work without the configured approval.'
  ),
  'strategy-view': guide(
    'Create and activate a plan so its current strategy can be viewed as live operating context.',
    'Use Strategy View to inspect hypotheses, positioning, principles, risks, and links to supporting evidence.',
    'Read the current Pabs Strategy View for [plan]. Explain the direction relevant to [work], including assumptions, boundaries, and unresolved decisions.',
    'Check that the response reflects the active version and clearly separates approved direction from hypotheses.'
  ),
  'strategy-output': guide(
    'Activate the strategy and choose the audience and purpose for the output.',
    'Generate a focused strategy output in Pabs, inspect its scope, then share or export the approved version.',
    'Use Pabs to prepare a strategy output for [audience] to support [decision or work]. Include only relevant approved context and flag gaps.',
    'Verify the audience, purpose, strategy version, included evidence, and unresolved questions.'
  ),
  'readme-generator': guide(
    'Activate the plan and make sure the product strategy and current delivery decisions are complete enough for builders.',
    'Generate the README in Pabs and review the product intent, setup context, scope, and constraints before placing it in the repository.',
    'Use Pabs to generate or update the project README for [repository]. Preserve technical setup and add the current product intent, scope, constraints, and decision links.',
    'Check that the README helps a new builder act correctly without exposing private plan data or inventing technical facts.'
  ),
  'statement-of-work': guide(
    'Define the approved scope, deliverables, responsibilities, timing, constraints, and acceptance evidence in the plan.',
    'Generate the Statement of Work in Pabs and review every commitment before sharing it externally.',
    'Use Pabs to prepare a Statement of Work for [engagement]. Include approved scope, exclusions, deliverables, responsibilities, milestones, assumptions, and acceptance criteria.',
    'Verify that commercial commitments match approved decisions and that unknowns remain explicit.'
  ),
  'learnings-how-it-works': guide(
    'Activate the plan and connect the sources or workspaces where evidence will be produced.',
    'Capture a learning, inspect the proposed impact, and approve, edit, or reject each resulting change.',
    'Use Pabs to process this evidence: [evidence]. Deduplicate it, assess quality, link affected assumptions and decisions, and propose changes for review.',
    'Check that evidence remains traceable and that no approved strategy changes without human confirmation.',
    { presentation: 'reference' }
  ),
  'design-authority-reference': guide(
    'Activate Design Authority and connect the sources required by the jobs you intend to run.',
    'Use this reference to choose a job in Pabs Chat or understand the exact MCP capability used by a connected client.',
    'Use Pabs Design Authority to [job] for [scope]. Apply [sources and constraints], require [evidence], and return a reviewable recommendation.',
    'Confirm the selected job, scope, evidence, authority boundary, and durable result before acting on it.',
    { presentation: 'reference' }
  ),
  'trust-model': guide(
    'Assign plan and Design Authority roles, then decide which actions are advisory and which require explicit approval.',
    'Use Pabs to review proposals, evidence, exceptions, and promotion decisions. Keep consequential authority with named people.',
    'Use Pabs to explain the authority boundary for [work]. Tell me what the agent may read, propose, or execute, and which actions require human approval.',
    'Verify identity, scope, evidence, and approval status in the decision trail.',
    { presentation: 'reference' }
  ),
  'integrations-setup': guide(
    'Create or open the plan you want the external client to use. Each plan has its own secure MCP connection URL.',
    'Open Share & Connect, choose the AI handoff, and copy the connection URL for that plan.',
    'After connecting, ask Pabs to identify this plan and summarise its current strategy, authority, and available jobs.',
    'Verify that the client identifies the correct plan and can read context without exposing credentials in chat or source control.'
  ),
  'integrate-claude': guide(
    'Copy the plan-specific MCP URL from Pabs and have Claude Code or Claude Desktop available.',
    'Add Pabs as an MCP server in Claude, reload the client, and verify the plan before starting work.',
    'Use Pabs to brief this Claude session on [work]. Read the current product intent, relevant decisions, Design Authority rules, and evidence gaps before proposing changes.',
    'Claude should identify the correct plan, cite current Pabs context, and expose the relevant Pabs capabilities.'
  ),
  'integrate-cursor': guide(
    'Copy the plan-specific MCP URL from Pabs and open the Cursor project that will use it.',
    'Add the Pabs server in Cursor MCP settings, reload the connection, and verify the plan.',
    'Use Pabs to brief this Cursor session before editing [area]. Summarise approved intent, protected constraints, relevant decisions, and review requirements.',
    'Cursor should read the correct plan and keep its implementation proposal within the approved boundaries.'
  ),
  'integrate-replit': guide(
    'Copy the plan-specific MCP URL and open the Replit workspace for the product.',
    'Add Pabs through Replit MCP support where available. If the workspace cannot connect, use an approved Pabs export as the handoff.',
    'Use Pabs to prepare this Replit build session for [feature]. Include intent, scope, constraints, acceptance criteria, and evidence required for review.',
    'Verify the build starts from current context and that new evidence can return to the same plan.'
  ),
  'integrate-lovable': guide(
    'Open the Pabs plan and prepare the approved brief or export required by the Lovable project.',
    'Connect Pabs through MCP when supported. Otherwise provide a current, scoped Pabs handoff at the start of the session.',
    'Use Pabs to prepare a Lovable-ready brief for [experience]. Include audience, intent, flows, states, design constraints, and review criteria.',
    'Check that generated work matches the brief and return implementation evidence to Pabs for review.'
  ),
  'integrate-figma-make': guide(
    'Activate Design Authority, connect design sources, and copy the plan-specific MCP URL.',
    'Connect Pabs to the client hosting the Figma Make workflow, then load the relevant product and design context.',
    'Use Pabs to brief a Figma Make session for [experience]. Include journeys, states, design-system rules, accessibility expectations, and the next review gate.',
    'Verify the generated concept against the approved intent before treating it as delivery-ready.'
  ),
  'integrate-google-stitch': guide(
    'Prepare an approved Pabs brief and the design context required for the Stitch exploration.',
    'Use a scoped Pabs export or connected client to bring product intent into Stitch, then return findings and artifacts to Pabs.',
    'Prepare a Stitch prompt for [experience] using the current Pabs intent, audience, flows, constraints, and review criteria. Do not include private evidence.',
    'Review generated concepts in Pabs before moving them into design or implementation.'
  ),
  'integrate-figma': guide(
    'Connect both Figma and the plan-specific Pabs MCP server to the same compatible AI client.',
    'Use Figma for design artifacts and Pabs for product intent, authority, decisions, and review criteria.',
    'Read [Figma artifact] and use Pabs to review it against the current product intent, Design Authority rules, accessibility expectations, and evidence.',
    'Confirm that findings reference the correct Figma artifact and the correct Pabs plan version.'
  ),
  'integrate-vibe-coding': guide(
    'Prepare a current Pabs brief or export for the product area before opening the build tool.',
    'Connect through MCP where supported. Otherwise begin with a scoped Pabs handoff and return evidence after the build.',
    'Use the current Pabs context to build [feature] in [tool]. Preserve [constraints], meet [acceptance criteria], and produce evidence for [review job].',
    'Check the generated work against approved strategy and design direction before promotion.'
  ),
  'integrate-other': guide(
    'Confirm the target client supports remote MCP and copy the plan-specific URL from Pabs.',
    'Add Pabs using the client configuration format shown below, reload the client, and verify the selected plan.',
    'Use Pabs to identify the connected plan, list the relevant product and Design Authority context, and recommend the next job for [work].',
    'Verify the connection, plan identity, capability list, and authority boundary before sharing sensitive context.'
  ),
  connectors: guide(
    'Choose the external knowledge source, confirm access, and decide which plan or authority area may use it.',
    'Connect the source in Pabs, review what it exposes, and assign it to the relevant strategy or Design Authority coverage.',
    'Use Pabs to find evidence about [question] in the approved connected sources. Cite the source, date, and any access or evidence limitations.',
    'Check provenance, freshness, permissions, and whether the source is authoritative for the decision.'
  ),
  'ask-pabs-agent': guide(
    'Connect an MCP client to the correct plan and confirm that the Pabs tools are available.',
    'Call ask_pabs_agent when the client needs interpretation, advice, or orchestration rather than a raw plan field.',
    'Ask Pabs: [question]. Use the current plan context and approved sources. Separate facts, inferences, recommendations, and actions requiring approval.',
    'Inspect the structured response, cited context, proposed action, and approval requirement before continuing.'
  ),
  'design-authority-connect': guide(
    'Complete Design Authority setup, connect its sources, and copy the plan-specific MCP URL.',
    'Add Pabs to the AI workspace where design or implementation happens, then verify the active authority contract.',
    'Use Pabs to summarise the Design Authority contract for this plan, including enabled jobs, lifecycle coverage, sources, protected constraints, and approval rules.',
    'Verify that the client sees the correct contract before asking it to prepare or review work.'
  ),
};
